// PURPOSE: Explain and connect keyboard triage on Outreach lists.
// CONTRACT: Spec 078 B1; only server-offered actions run, existing controls keep disabled states.
// DEPENDS: Row cursor, shared Dialog and lead action hook. CEILING: No new API or authorization rules.
import { useState, type ReactNode } from 'react';
import { Dialog, ErrorNotice } from './components';
import { useRowCursor } from './outreach-keys';
import { useLeadAction } from './outreach-client';
import type { NativeRecord } from './types';

export function OutreachShortcuts({ onClose }: { onClose(): void }) {
 return <Dialog title="Outreach shortcuts" onClose={onClose}><dl className="outreach-shortcuts">{[
  ['j / Down', 'Next lead'], ['k / Up', 'Previous lead'], ['Enter', 'Expand or collapse details'],
  ['c', 'Claim lead'], ['p', 'Pass lead'], ['s', 'Toggle star'], ['x', 'Toggle checkbox'],
  ['?', 'Show shortcuts'], ['Esc', 'Clear cursor or close this sheet'], ['Ctrl / Cmd + Z', 'Undo latest action'],
 ].map(([key, label]) => <div key={key}><dt><kbd>{key}</kbd></dt><dd>{label}</dd></div>)}</dl><p className="small muted">Actions run only when available for the selected lead.</p></Dialog>;
}

export function OutreachKeyboardList({ records, extraIds = [], disabled = false, children }: { records: NativeRecord[]; extraIds?: string[]; disabled?: boolean; children: ReactNode }) {
 const [help, setHelp] = useState(false);
 const { busy, error, act } = useLeadAction();
 const cursor = useRowCursor([...records.map(record => record.id), ...extraIds], (key, id) => {
  if (key === '?') { setHelp(true); return; }
  const row = Array.from(cursor.ref.current?.querySelectorAll<HTMLElement>('[data-row-id]') || []).find(row => row.dataset.rowId === id);
  if (!row || disabled || busy || row.getAttribute('aria-busy') === 'true') return;
  if (key === 'Enter') { row.querySelector<HTMLElement>('[data-row-expand], summary')?.click(); return; }
  if (key === 'x') { row.querySelector<HTMLInputElement>('input[type="checkbox"]:not(:disabled)')?.click(); return; }
  const action = ({ c: 'accept', p: 'reject', s: 'star' } as Record<string, string>)[key];
  const record = records.find(record => record.id === id);
  if (!action || !record?.allowed_actions.includes(action)) return;
  const button = row.querySelector<HTMLButtonElement>(`[data-lead-action="${action}"]`);
  if (button) { button.click(); return; }
  void act(record, action);
 });
 return <div ref={cursor.ref} aria-busy={busy}>{children}{!!error && <ErrorNotice error={error} />}<p className="outreach-key-footer small muted">j k move · c claim · p pass · s star · ? help</p>{help && <OutreachShortcuts onClose={() => setHelp(false)} />}</div>;
}
